import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { GitBranch } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { RatePlanInfo } from '@/types/shared'

interface DerivedPlanState {
  offsetType: 'percentage' | 'absolute'
  offsetValue: string
}

interface DerivedPlanInput {
  offset_type: 'percentage' | 'absolute'
  offset_value: string
}

interface DerivedRatePlansModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roomTypeId: number
  leadPlanId: number
  ratePlans: RatePlanInfo[]
  /**
   * Optional preset for derived plan offsets (from parent's staged state).
   * When omitted, the modal initializes from rate plan offsetType/offsetValue.
   */
  initialDerivedPlans?: Record<string, DerivedPlanInput>
  currencySymbol: string
  onSave: (roomTypeId: number, derivedPlans: Record<string, DerivedPlanInput>) => void
}

export default function DerivedRatePlansModal({
  open,
  onOpenChange,
  roomTypeId,
  leadPlanId,
  ratePlans,
  initialDerivedPlans,
  currencySymbol: sym,
  onSave,
}: DerivedRatePlansModalProps) {
  // Capture lead plan ID in state so it's stable during save (props can change between opens)
  const [capturedLeadPlanId, setCapturedLeadPlanId] = useState(leadPlanId)
  const leadPlan = ratePlans.find((rp) => rp.id === capturedLeadPlanId)
  const derivedPlans = ratePlans.filter((rp) => rp.id !== capturedLeadPlanId)
  const hasUnpricedPlan = derivedPlans.some((rp) => rp.isUnpriced ?? rp.offsetType == null)

  function buildInitialState(): Record<string, DerivedPlanState> {
    const result: Record<string, DerivedPlanState> = {}
    const nonLeadPlans = ratePlans.filter((rp) => rp.id !== leadPlanId)
    for (const rp of nonLeadPlans) {
      const presetKey = String(rp.id)
      const preset = initialDerivedPlans?.[presetKey]
      if (preset) {
        result[presetKey] = {
          // Preserve an explicit absolute offset; default everything else to
          // percentage (industry standard — see CLAUDE.md).
          offsetType: preset.offset_type === 'absolute' ? 'absolute' : 'percentage',
          offsetValue: preset.offset_value,
        }
      } else {
        result[presetKey] = {
          offsetType: rp.offsetType === 'absolute' ? 'absolute' : 'percentage',
          offsetValue: rp.offsetValue != null ? String(rp.offsetValue) : '0',
        }
      }
    }
    return result
  }

  const [planStates, setPlanStates] = useState(buildInitialState)

  // Re-sync only when modal transitions from closed to open.
  const prevOpenRef = React.useRef(false)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setCapturedLeadPlanId(leadPlanId)
      setPlanStates(buildInitialState())
    }
    prevOpenRef.current = open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleToggleType(planId: string, newType: 'percentage' | 'absolute') {
    setPlanStates((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], offsetType: newType },
    }))
  }

  function handleValueChange(planId: string, value: string) {
    setPlanStates((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], offsetValue: value },
    }))
  }

  function handleSave() {
    if (!capturedLeadPlanId) return
    const payload: Record<string, DerivedPlanInput> = {}
    for (const [planId, state] of Object.entries(planStates)) {
      payload[planId] = {
        offset_type: state.offsetType,
        offset_value: state.offsetValue || '0',
      }
    }
    onSave(roomTypeId, payload)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-primary" />
            Derived Rate Plans
          </DialogTitle>
          <DialogDescription>
            Lead plan: <span className="font-medium text-foreground">{leadPlan?.name}</span>
            {leadPlan && (
              <span className="ml-1.5 text-muted-foreground">
                (Occ {leadPlan.referenceOccupancy})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {derivedPlans.map((rp) => {
            const planId = String(rp.id)
            const state = planStates[planId] || { offsetType: 'percentage' as const, offsetValue: '0' }
            const isPercentage = state.offsetType === 'percentage'
            // "Not priced yet" = no offset on record. Prefer the backend flag (rooms-setup);
            // fall back to a null offset for surfaces that don't send it (onboarding review).
            const notPriced = rp.isUnpriced ?? rp.offsetType == null

            return (
              <div
                key={rp.id}
                className={cn(
                  'rounded-lg border bg-white p-3.5 space-y-2.5',
                  notPriced ? 'border-amber-300/70 ring-1 ring-amber-100' : 'border-border/60'
                )}
              >
                {/* Plan name + reference occupancy */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate text-sm font-medium text-foreground">{rp.name}</span>
                    {notPriced && (
                      <Badge
                        variant="secondary"
                        className="shrink-0 border-transparent bg-amber-100 text-[11px] font-medium text-amber-700"
                      >
                        Not priced yet
                      </Badge>
                    )}
                  </div>
                  <Badge
                    variant="secondary"
                    className="shrink-0 border-transparent bg-slate-100 text-[11px] font-medium text-slate-600"
                  >
                    Occ {rp.referenceOccupancy}
                  </Badge>
                </div>

                {/* Offset controls */}
                <div className="flex items-center gap-2.5">
                  {/* Type toggle */}
                  <div className="inline-flex rounded-md border border-border/60 bg-muted/50 p-0.5">
                    <button
                      type="button"
                      onClick={() => handleToggleType(planId, 'percentage')}
                      className={cn(
                        'inline-flex items-center rounded-[5px] px-2.5 py-1 text-[11px] font-semibold transition-all cursor-pointer',
                        isPercentage
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleType(planId, 'absolute')}
                      className={cn(
                        'inline-flex items-center rounded-[5px] px-2.5 py-1 text-[11px] font-semibold transition-all cursor-pointer',
                        !isPercentage
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {sym}
                    </button>
                  </div>

                  {/* Value input */}
                  <Input
                    type="number"
                    step={isPercentage ? '1' : '0.01'}
                    className="w-28 text-center rounded-lg border-border/60 tabular-nums"
                    placeholder="0"
                    value={state.offsetValue}
                    onChange={(e) => handleValueChange(planId, e.target.value)}
                  />

                  {/* Preview label */}
                  <span className="text-xs text-muted-foreground">
                    {isPercentage
                      ? `${parseFloat(state.offsetValue || '0') >= 0 ? '+' : ''}${state.offsetValue || '0'}% from lead`
                      : `${parseFloat(state.offsetValue || '0') >= 0 ? '+' : ''}${sym}${state.offsetValue || '0'} from lead`}
                  </span>
                </div>
              </div>
            )
          })}

          {derivedPlans.length === 0 && (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <GitBranch className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No derived plans</p>
              <p className="mt-1 text-xs text-muted-foreground">
                This room type only has one active rate plan.
              </p>
            </div>
          )}

          {hasUnpricedPlan && (
            <p className="text-xs text-muted-foreground">
              Until you set an offset, the engine does not price these plans. Choose a discount for
              non-refundable rates (e.g. −10%), a premium for packages, or leave 0% to match the lead exactly.
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={derivedPlans.length === 0}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
